// material-ui
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Modal,
    Checkbox,
    Grid,
    ToggleButtonGroup,
    ToggleButton,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    CircularProgress,
    Switch
} from '@mui/material';
import { ManageAccounts, Cancel } from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { useState, useEffect } from 'react';
import TasklistFilter from './TasklistFilter';
import { deepPurple, grey } from '@mui/material/colors';
import useFetchData from 'hooks/useFetchData';
import { axiosInstance } from 'utils/axios';
import { useSelector } from 'react-redux';
import { selectCurrentToken } from 'features/auth/authSlice';
import { successAlert, errorAlert, warningAlert } from 'ui-component/Toasts';
import { useTranslation } from "react-i18next";

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButtonGroup-grouped': {
        //   margin: theme.spacing(2),

        //   '&.Mui-disabled': {
        //     border: 0,
        //   },
        '&:not(:first-of-type)': {
            borderRadius: theme.shape.borderRadius
        },
        '&:first-of-type': {
            borderRadius: theme.shape.borderRadius
        }
    }
}));

const StyledToggleButton = styled(ToggleButton)({
    border: 2,
    borderColor: deepPurple[500],
    backgroundColor: grey[300],
    '&.Mui-selected, &.Mui-selected:hover': {
        color: 'white',
        backgroundColor: deepPurple[500]
    }
});

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 1000,
    width: '90%',
    bgcolor: 'background.paper',
    boxShadow: 24,
    padding: '20px',
    borderRadius: '10px'
};

const TransactionModal = ({
    transactionModalOpen,
    setTransactionModalOpen,
    activeTab,
    buttonName,
    customerRow,
    fetchCustomers,
    handleRefresh
}) => {
    const { t } = useTranslation();
    const token = useSelector(selectCurrentToken);

    const [view, setView] = useState('deposit');
    const [customerID, setCustomerID] = useState(0);
    const [walletBalance, setWalletBalance] = useState(0);
    const [walletBalanceLoading, setWalletBalanceLoading] = useState(false);
    const [cappingAmountDetails, setCappingAmountDetails] = useState();
    const [cappingAmountLoading, setCappingAmountLoading] = useState(false);

    const [depositTabUpdateButtonDisabled, setDepositTabUpdateButtonDisabled] = useState(false);

    const handleChange = (event, nextView) => {
        setView(nextView);
    };

    const handleOpenModal = () => {
        setView(activeTab);
        setTransactionModalOpen(true);
    };
    
    useEffect(() => {
        if (transactionModalOpen && customerRow?.id) {
            setCustomerID(customerRow.id);
            setView(activeTab); // or set to 'resetTasklist' by default
            getWalletBalance();
            getCustomerCappingAmountDetails();
        }
    }, [transactionModalOpen, customerRow]);

    const getWalletBalance = async () => {
        try {
            setCappingAmountLoading(true);
            const res = await axiosInstance.get(
                `/Backend/Customer/SingleWalletBalance?customerID=${customerRow.id}&walletTypeID=11`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            if (res.status === 200) {
                setWalletBalance(res.data ?? 0);
            } else {
                errorAlert('发生错误');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setCappingAmountLoading(false);
        }
    };

    const getCustomerCappingAmountDetails = async () => {
        try {
            setWalletBalanceLoading(true);
            const res = await axiosInstance.get(
                `/Backend/Customer/CappingAmountDetails?customerID=${customerRow.id}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );
            if (res.status === 200) {
                setCappingAmountDetails(res.data);
            } else {
                errorAlert('发生错误');
            }
        } catch (error) {
            console.log(error);
        } finally {
            setWalletBalanceLoading(false);
        }
    };

    function DepositTab() {
        const [depositAmount, setDepositAmount] = useState(0);
        const [isHideFrontend, setIsHideFrontend] = useState(false);

        const [insertDepositButtonDisabled, setInsertDepositButtonDisabled] = useState(false);
        const submitInsertDeposit = async () => {
            if (insertDepositButtonDisabled) return;

            setInsertDepositButtonDisabled(true);

            try {
                const res = await axiosInstance.post(
                    '/Deposit',
                    {
                        customerID: customerRow.id,
                        amount: depositAmount,
                        statusID: 2,
                        isHideFrontend: false
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    successAlert('成功充值');
                    // setTransactionModalOpen(false);
                    // fetchCustomers();
                    handleRefresh();
                    getWalletBalance();
                    getCustomerCappingAmountDetails();
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setInsertDepositButtonDisabled(false);
            }
        };

        const handleToggle = (event) => {
            setIsHideFrontend(event.target.checked);  // Event returns a 'checked' property
        };

        return (
            <Box>
                {walletBalanceLoading ? (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {t("customer.walletBalance")} : {walletBalance ? walletBalance.toLocaleString(2) : '0'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )}
                <Grid item container xs={12} sx={{ mt: 2 }}>
                    <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t("customer.amount")} :
                            </Typography>
                            <TextField 
                                type="number"
                                id="outlined-basic" 
                                variant="outlined" 
                                value={depositAmount}
                                onChange={(e) => setDepositAmount(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        submitInsertDeposit();
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Button
                    variant="contained"
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        width: '150px',
                        backgroundColor: deepPurple[500]
                    }}
                    onClick={submitInsertDeposit}
                    disabled={insertDepositButtonDisabled}
                >
                    {t("customer.confirmDeposit")}
                </Button>
            </Box>
        );
    }

    function WithdrawalTab() {
        const [withdrawalAmount, setWithdrawalAmount] = useState(0);
        const [isHideFrontend, setIsHideFrontend] = useState(false);

        const [insertWithdrawalButtonDisabled, setInsertWithdrawalButtonDisabled] = useState(false);
        const submitInsertWithdrawal = async () => {
            if (insertWithdrawalButtonDisabled) return;

            setInsertWithdrawalButtonDisabled(true);

            try {
                const res = await axiosInstance.post(
                    '/Withdrawal',
                    {
                        customerID: customerRow.id,
                        amount: withdrawalAmount,
                        statusID: 2,
                        isHideFrontend: false
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    successAlert('成功提款');
                    // setTransactionModalOpen(false);
                    // fetchCustomers();
                    handleRefresh();
                    getWalletBalance();
                    getCustomerCappingAmountDetails();
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setInsertWithdrawalButtonDisabled(false);
            }
        };

        const handleToggle = (event) => {
            setIsHideFrontend(event.target.checked);  // Event returns a 'checked' property
        };

        return (
            <Box>
                {walletBalanceLoading ? (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {t("customer.walletBalance")} : {walletBalance ? walletBalance.toLocaleString(2) : '0'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )}
                <Grid item container xs={12} sx={{ mt: 2 }}>
                    <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t("customer.amount")} :
                            </Typography>
                            <TextField 
                                type="number"
                                id="outlined-basic" 
                                variant="outlined" 
                                value={withdrawalAmount}
                                onChange={(e) => setWithdrawalAmount(e.target.value)} 
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        submitInsertWithdrawal();
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Button
                    variant="contained"
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        width: '150px',
                        backgroundColor: deepPurple[500]
                    }}
                    onClick={submitInsertWithdrawal}
                    disabled={insertWithdrawalButtonDisabled}
                >
                    {t("customer.confirmWithdrawal")}
                </Button>
            </Box>
        );
    }

    function AdjustmentTab() {
        const [adjustmentAmount, setAdjustmentAmount] = useState(0);
        const [isHideFrontend, setIsHideFrontend] = useState(false);

        const [insertAdjustmentButtonDisabled, setInsertAdjustmentButtonDisabled] = useState(false);
        const submitInsertAdjustment = async () => {
            if (insertAdjustmentButtonDisabled) return;

            setInsertAdjustmentButtonDisabled(true);

            try {
                const res = await axiosInstance.post(
                    '/AdjustCustomerWalletBalance',
                    {
                        customerID: customerRow.id,
                        amount: adjustmentAmount,
                        walletTypeID: 11,
                        remark: '',
                        isHideFrontend: false
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    successAlert('成功创建彩金');
                    // setTransactionModalOpen(false);
                    // fetchCustomers();
                    handleRefresh();
                    getWalletBalance();
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setInsertAdjustmentButtonDisabled(false);
            }
        };

        const handleToggle = (event) => {
            setIsHideFrontend(event.target.checked);  // Event returns a 'checked' property
        };

        return (
            <Box>
                {walletBalanceLoading ? (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {t("customer.walletBalance")} : {walletBalance ? walletBalance.toLocaleString(2) : '0'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )}
                <Grid item container xs={12} sx={{ mt: 2 }}>
                    <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t("customer.amount")} :
                            </Typography>
                            <TextField 
                                type="number"
                                id="outlined-basic" 
                                variant="outlined" 
                                value={adjustmentAmount}
                                onChange={(e) => setAdjustmentAmount(e.target.value)} 
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        submitInsertAdjustment();
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Button
                    variant="contained"
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        width: '150px',
                        backgroundColor: deepPurple[500]
                    }}
                    onClick={submitInsertAdjustment}
                    disabled={insertAdjustmentButtonDisabled}
                >
                    {t("customer.confirmAdjustment")}
                </Button>
            </Box>
        );
    }

    function AdjustNegativeTab() {
        const [adjustmentAmount, setAdjustmentAmount] = useState(0);
        const [remark, setRemark] = useState('');
        const [isHideFrontend, setIsHideFrontend] = useState(false);

        const [insertAdjustmentButtonDisabled, setInsertAdjustmentButtonDisabled] = useState(false);
        const submitInsertAdjustment = async () => {
            if (insertAdjustmentButtonDisabled) return;
            
            setInsertAdjustmentButtonDisabled(true);

            try {
                const res = await axiosInstance.post(
                    '/AdjustCustomerWalletBalance',
                    {
                        customerID: customerRow.id,
                        amount: adjustmentAmount,
                        walletTypeID: 11,
                        remark: remark,
                        adjustmentTypeID: 1,
                        isHideFrontend: isHideFrontend
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    successAlert('成功调整负数');
                    // setTransactionModalOpen(false);
                    // fetchCustomers();
                    handleRefresh();
                    getWalletBalance();
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setInsertAdjustmentButtonDisabled(false);
            }
        };

        const handleToggle = (event) => {
            setIsHideFrontend(event.target.checked);  // Event returns a 'checked' property
        };

        return (
            <Box>
                {walletBalanceLoading ? (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {t("customer.walletBalance")} : {walletBalance ? walletBalance.toLocaleString(2) : '0'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )}
                <Grid item container xs={12} sx={{ mt: 2 }}>
                    <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t("customer.isHideFrontend")} :
                            </Typography>
                            <Switch
                                checked={isHideFrontend}
                                onChange={handleToggle}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: deepPurple[500], // Purple when the switch is in the 'on' state
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: deepPurple[500], // Purple track when the switch is 'on'
                                    },
                                    '& .MuiSwitch-switchBase': {
                                        color: 'gray', // Gray when the switch is in the 'off' state
                                    },
                                    '& .MuiSwitch-switchBase + .MuiSwitch-track': {
                                        backgroundColor: 'lightgray', // Light gray track when the switch is 'off'
                                    },
                                }}
                                inputProps={{ 'aria-label': 'controlled' }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Grid item container xs={12} sx={{ mt: 2 }}>
                    <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t("customer.amount")} :
                            </Typography>
                            <TextField 
                                type="number"
                                id="outlined-basic" 
                                variant="outlined" 
                                value={adjustmentAmount}
                                onChange={(e) => setAdjustmentAmount(e.target.value)} 
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        submitInsertAdjustment();
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                    <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t("customer.remark")} :
                            </Typography>
                            <TextField 
                                id="outlined-basic" 
                                variant="outlined" 
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)} 
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        submitInsertAdjustment();
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Button
                    variant="contained"
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        width: '150px',
                        backgroundColor: deepPurple[500],
                        marginTop: '30px'
                    }}
                    onClick={submitInsertAdjustment}
                    disabled={insertAdjustmentButtonDisabled}
                >
                    {t("customer.confirmNegativeAdjustment")}
                </Button>
            </Box>
        );
    }

    function VIPLevelTab() {
        const { data: ambassadorLevels } = useFetchData('/AmbassadorLevels/All');
        const [vipLevelID, setVIPLevelID] = useState(0);

        const [updateButtonDisabled, setUpdateButtonDisabled] = useState(false);

        const submitUpdateVIPLevel = async () => {
            if (updateButtonDisabled) return;
            
            setUpdateButtonDisabled(true);

            try {
                const res = await axiosInstance.put(
                    '/Customer/AmbassadorLevel',
                    {
                        customerID: customerRow.id,
                        ambassadorLevelID: vipLevelID
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    successAlert('成功更新会员等级');
                    // setTransactionModalOpen(false);
                    // fetchCustomers();
                    handleRefresh();
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setUpdateButtonDisabled(false);
            }
        };

        return (
            <Box>
                <Grid item container xs={12} sx={{ mt: 2 }}>
                    <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t("customer.level")} :
                            </Typography>
                            <Select
                                displayEmpty
                                value={vipLevelID}
                                onChange={(e) => setVIPLevelID(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault(); // optional, depending on your UX
                                        submitUpdateVIPLevel();
                                    }
                                }}
                            >
                                <MenuItem sx={{ width: '100%' }} value={0}>
                                    {t("customer.pleaseSelectVIPLevel")}
                                </MenuItem>
                                {ambassadorLevels.map((item, index) => (
                                    <MenuItem sx={{ width: '100%' }} value={item.id} key={index}>
                                        {item.ambassadorLevelName} - $ {item.cashBonusGiven}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                    </Grid>
                </Grid>
                <Button
                    variant="contained"
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        width: '150px',
                        backgroundColor: deepPurple[500]
                    }}
                    onClick={submitUpdateVIPLevel}
                    disabled={updateButtonDisabled}
                >
                    {t("customer.confirmUpdateVIPLevel")}
                </Button>
            </Box>
        );
    }

    function ManualDailyCheckInTab() {
        const { data: dailyCheckInProgramDetails } = useFetchData(`/CustomerDailyCheckInProgramDetails/All?customerID=${customerRow.id}`);

        const [updateButtonDisabled, setUpdateButtonDisabled] = useState(false);

        const submitManualInsertDailyCheckIn = async (programID) => {
            if (updateButtonDisabled) return;
            
            setUpdateButtonDisabled(true);

            try {
                const res = await axiosInstance.post(
                    '/DailyCheckIn/Manual',
                    {
                        customerID: customerRow.id,
                        programID: programID
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    successAlert('成功手动每日签到');
                    // setTransactionModalOpen(false);
                    // fetchCustomers();
                    handleRefresh();
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setUpdateButtonDisabled(false);
            }
        };

        return (
            <Box>
                <Grid item container xs={12} sx={{ mt: 2 }}>
                    <Grid item container xs={12} md={12} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                        <Paper sx={{ width: '100%', overflow: 'hidden', marginTop: '20px' }}>
                            <TableContainer sx={{ height: '80vh' }}>
                                <Table stickyHeader aria-label="sticky table">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell key='dayNumber' style={{ width: 170 }}>{t("masterData.dayNumber")}</TableCell>
                                            <TableCell key='bonusAmountGiven' style={{ width: 170 }}>{t("masterData.amount")}</TableCell>
                                            <TableCell key='setting' style={{ width: 250 }}></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dailyCheckInProgramDetails.map((row) => {
                                            return (
                                                <TableRow hover role="checkbox" tabIndex={-1} key={row.id}>
                                                    <TableCell key='dayNumber' style={{fontSize: "12px"}}>
                                                        {row.dayNumber}
                                                    </TableCell>
                                                    <TableCell key='bonusAmountGiven' style={{fontSize: "12px"}}>
                                                        {row.bonusAmountGiven}
                                                        {row.customerBonusAmountGiven === null ? <>&#10060;</> : <>&#9989;</> }
                                                    </TableCell>
                                                    <TableCell key='setting' style={{fontSize: "12px"}}>
                                                        <Box sx={{ alignItems: 'center', display: 'flex' }}>
                                                            <Button
                                                                variant="contained"
                                                                sx={{
                                                                    display: 'block',
                                                                    mx: 'auto',
                                                                    width: '150px',
                                                                    backgroundColor: deepPurple[500]
                                                                }}
                                                                onClick={() => submitManualInsertDailyCheckIn(row.id)}
                                                                disabled={updateButtonDisabled}
                                                            >
                                                                {t("customer.confirmInsertDailyCheckIn")}
                                                            </Button>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        );
    }

    function CappingTab() {
        const [cappingAmount, setCappingAmount] = useState(0);

        const [insertCappingButtonDisabled, setInsertCappingButtonDisabled] = useState(false);
        const submitInsertCapping = async () => {
            if (insertCappingButtonDisabled) return;

            setInsertCappingButtonDisabled(true);

            try {
                const res = await axiosInstance.post(
                    '/CustomerCappingAmount',
                    {
                        customerID: customerRow.id,
                        amount: cappingAmount
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    }
                );
                if (res.status === 200) {
                    successAlert('成功加封顶数额');
                    handleRefresh();
                    getWalletBalance();
                    getCustomerCappingAmountDetails();
                } else {
                    errorAlert('发生错误');
                }
            } catch (error) {
                console.log(error);
            } finally {
                setInsertCappingButtonDisabled(false);
            }
        };

        return (
            <Box>
                {walletBalanceLoading ? (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {t("customer.walletBalance")} : {walletBalance ? walletBalance.toLocaleString(2) : '0'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )}
                {/* {cappingAmountLoading ? (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <CircularProgress />
                            </Box>
                        </Grid>
                    </Grid>
                ) : (
                    <Grid item container xs={12} sx={{ mt: 2 }}>
                        <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {t("customer.capping")} : {cappingAmountDetails ? `${cappingAmountDetails.currentCappingAmount} / ${cappingAmountDetails.maxCappingAmount}` : '0 / 0'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                )} */}
                <Grid item container xs={12} sx={{ mt: 2 }}>
                    <Grid item container xs={12} md={6} sx={{ mt: 3 }} pr={{ md: '5px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Typography
                                sx={{
                                    fontSize: '16px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {t("customer.amount")} :
                            </Typography>
                            <TextField 
                                type="number"
                                id="outlined-basic" 
                                variant="outlined" 
                                value={cappingAmount}
                                onChange={(e) => setCappingAmount(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        submitInsertCapping();
                                    }
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
                <Button
                    variant="contained"
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        width: '150px',
                        backgroundColor: deepPurple[500]
                    }}
                    onClick={submitInsertCapping}
                    disabled={insertCappingButtonDisabled}
                >
                    {t("customer.confirmInsertCapping")}
                </Button>
            </Box>
        );
    }

    return (
        <>
            {/* <Button
                onClick={handleOpenModal}
                sx={{
                    border: 'none',
                    backgroundColor: deepPurple[500],
                    width: 'fit-content',
                    height: 'fit-content',
                    color: 'white',
                    '&.Mui-selected, &.Mui-selected:hover': {
                        color: deepPurple[500],
                        backgroundColor: 'white'
                    },
                    fontSize: '12px'
                }}
            >
                {buttonName}
            </Button> */}
            <Modal
                open={transactionModalOpen}
                onClose={() => setTransactionModalOpen(false)}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Typography id="modal-modal-title" variant="h3" component="h2">
                            {t("customer.editBalanceDetails")}
                        </Typography>
                        <Cancel onClick={() => setTransactionModalOpen(false)} sx={{ width: '30px', height: '30px' }} />
                    </Box>
                    <Box
                        id="modal-modal-description"
                        sx={{
                            overflowY: 'auto'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%' 
                            }}
                        >
                            <StyledToggleButtonGroup
                                value={view}
                                exclusive
                                onChange={handleChange}
                                sx={{
                                    flexDirection: { xs: 'column', md: 'row' }
                                }}
                            >
                                <StyledToggleButton value="deposit" aria-label="deposit">
                                    {t("customer.deposit")}
                                </StyledToggleButton>
                                <StyledToggleButton value="adjustment" aria-label="adjustment">
                                    {t("customer.adjustment")}
                                </StyledToggleButton>
                                <StyledToggleButton value="withdrawal" aria-label="withdrawal">
                                    {t("customer.withdrawal")}
                                </StyledToggleButton>
                            </StyledToggleButtonGroup>
                        </Box>
            
                        {view === 'deposit' && (
                            <>
                                <DepositTab />
                            </>
                        )}
                        {view === 'withdrawal' && (
                            <>
                                <WithdrawalTab />
                            </>
                        )}
                        {view === 'adjustment' && (
                            <>
                                <AdjustmentTab />
                            </>
                        )}
                        {view === 'adjustNegative' && (
                            <>
                                <AdjustNegativeTab />
                            </>
                        )}
                        {view === 'vipLevel' && (
                            <>
                                <VIPLevelTab />
                            </>
                        )}
                        {view === 'dailyCheckIn' && (
                            <>
                                <ManualDailyCheckInTab />
                            </>
                        )}
                        {/* {view === 'capping' && (
                            <>
                                <CappingTab />
                            </>
                        )} */}
                    </Box>
                </Box>
            </Modal>
        </>
    );
};

export default TransactionModal;